import { BaseComponent } from '../../components/base-component'
import { RentalStep } from './rental-step'
import { MultiVariantStep } from './multi-variant-step'
import { MultiProductStep } from './multi-product-step'
import { NeckwareStep } from './neckware-step'
import { supercycleFetch } from './supercycle'
import { CartAPI } from '../../api/cart-api'
import { fetchConfig } from '../../utils/fetch-config'
import { CartDrawer } from '../cart-drawer/cart-drawer'
import { CartPage } from '../cart/cart'
import { BodyElement } from 'src/scripts/components/body-element/body-element'
import { CartNotificationPopup } from 'src/scripts/components/cart-notification/cart-notification-popup'
import { $el, $list } from 'src/scripts/utils/utils'
import { parseHTML, replaceNodeChildren } from 'src/scripts/utils/dom'
import {
  RentalIntentResponse,
  AvailabilityResponse,
  ConfiguratorStep
} from '../../types/rental'

const CART_DRAWER_SELECTOR = '#CartDrawer'
const CART_PAGE_SELECTOR = 'cart-page'
const CART_NOTIFICATION_POPUP_SELECTOR = '#CartNotificationPopup'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class ProductConfigurator extends BaseComponent {
  /** Array of all configuration steps in order */
  private steps: ConfiguratorStep[] = []
  /** Currently active/visible step */
  private activeStep: ConfiguratorStep | null = null
  private lastCheckedDates: { startDate: string; endDate: string } | null = null
  private selectedVariants: Record<string, number> = {}
  private cartApi: CartAPI = new CartAPI()
  private addToCartButton: HTMLButtonElement | null = null
  private lastParsedState: string
  private selectedProductDetails: Map<string, {
    title: string,
    options: Record<string, string>
  }> = new Map()
  private summaryList: HTMLUListElement | null = null

  async connectedCallback() {
    super.connectedCallback()
    this.summaryList = this.querySelector('.product-configurator__summary-list')

    // Handle step:complete events
    this.addEventListener('step:complete', (e: CustomEvent) => {
        console.log('ProductConfigurator caught step:complete event:', {
            detail: e.detail,
            target: e.target
        })

        const completedStep = e.target as ConfiguratorStep
        if (!completedStep) return

        // Complete the current step
        completedStep.deactivate()
        completedStep.complete()

        // Find and activate next step
        const nextStepIndex = completedStep.getIndex() + (e.detail.skipStep ? 1 : 0)
        const nextStep = this.steps.find(step => step.getIndex() === nextStepIndex)
        
        if (nextStep) {
            console.log('Activating next step:', nextStep)
            nextStep.activate()
            this.activeStep = nextStep
        } else {
            // This was the last step, update summary
            this.updateSummary()
        }

        // Update add to cart button state
        this.updateAddToCartButton()
    })
    
    // Add new event listener for product changes
    this.addEventListener('products:changed', () => {
        // Update the summary whenever products change
        this.updateSummary()
    })
    
    await this.initializeSteps()
    this.initializeListeners()
    this.initializeAddToCartButton()
  }

  private initializeAddToCartButton() {
    this.addToCartButton = this.querySelector('.product-configurator__add-to-cart-button')
    if (this.addToCartButton) {
      const buttonText = this.addToCartButton.querySelector('.product-configurator__add-to-cart-button-text')
      if (buttonText) {
        buttonText.textContent = 'Select Product(s)'
      }
      this.addToCartButton.disabled = true
      this.addListener(this.addToCartButton, 'click', this.handleAddToCart.bind(this))
    }
  }

  /**
   * Initializes all configuration steps
   * Finds and stores references to all step elements
   * Sets up the initial active step
   * @throws Error if required rental step is not found
   */
  private async initializeSteps() {
    try {
      await customElements.whenDefined('rental-step')
      await customElements.whenDefined('multi-variant-step')
      await customElements.whenDefined('multi-product-step')
      await customElements.whenDefined('neckware-step')

      // Initialize rental step (always first)
      const rentalStep = this.querySelector('rental-step')

      if (!rentalStep) {
        throw new Error('Required rental step not found')
      }
      this.steps.push(rentalStep as RentalStep)

      // Initialize multi-variant steps
      const multiVariantSteps = Array.from(this.querySelectorAll('multi-variant-step'))
      multiVariantSteps.forEach(step => {
        this.steps.push(step as MultiVariantStep)
      })

      // Initialize multi-product steps
      const multiProductSteps = Array.from(this.querySelectorAll('multi-product-step'))
      multiProductSteps.forEach(step => {
        this.steps.push(step as MultiProductStep)
      })

      // Initialize neckware step
      const neckwareStep = this.querySelector('neckware-step')
      if (neckwareStep) {
        this.steps.push(neckwareStep as NeckwareStep)
      }

      // Set initial active step
      this.activeStep = this.steps[0]
      this.activeStep.activate()

    } catch (error) {
      console.error('Failed to initialize steps:', error)
    }
  }

  /**
   * Sets up event listeners for step navigation
   * - Next button clicks for moving to next step
   * - Completed step clicks for moving back to previous steps
   */
  private initializeListeners() {
    // Next button clicks - find all next buttons in all steps
    const nextButtons = this.querySelectorAll('.product-configurator__step-footer-button')
    nextButtons.forEach(button => {
      this.addListener(button, 'click', this.handleNextClick.bind(this))
    })

    // Completed step clicks
    this.steps.forEach(step => {
      this.addListener(step, 'click', this.handleCompletedStepClick.bind(this))
    })
  }

  /**
   * Updates button state with loading class
   * @param button Button element to update
   * @param isLoading Whether to add or remove loading state
   */
  private setButtonLoading(button: HTMLButtonElement, isLoading: boolean) {
    button.classList.toggle('product-configurator__step-footer-button--loading', isLoading)
    button.disabled = isLoading
  }

  /**
   * Handles click events on the "Next" button
   * @param event Mouse event from button click
   */
  private async handleNextClick(event: MouseEvent) {
    event.stopPropagation()

    const button = event.currentTarget as HTMLButtonElement
    if (!this.activeStep) return

    // Check if step is valid before proceeding
    if (!this.activeStep.isValid()) {
      return
    }

    // If it's a rental step, check product availability
    if (this.activeStep instanceof RentalStep) {
      console.log('Clicked next button in rental step')
      const rentalStep = this.activeStep as RentalStep
      const { startDate, endDate } = rentalStep.getDates()

      // Only check availability if dates have changed
      const datesChanged = !this.lastCheckedDates ||
        this.lastCheckedDates.startDate !== startDate ||
        this.lastCheckedDates.endDate !== endDate

      if (datesChanged) {
        try {
          this.setButtonLoading(button, true)

          // Get product IDs from all variant steps
          const multiVariantIds = Array.from(this.querySelectorAll('multi-variant-step'))
            .map(step => (step as HTMLElement).dataset.productId)
          const singleVariantIds = Array.from(this.querySelectorAll('single-variant-step'))
            .map(step => (step as HTMLElement).dataset.productId)
          const multiProductIds = Array.from(this.querySelectorAll('multi-product-step .product-configurator__step-product'))
            .map(product => (product as HTMLElement).dataset.productId)
          const neckwareIds = Array.from(this.querySelectorAll('neckware-step .product-configurator__step-product'))
            .map(product => (product as HTMLElement).dataset.productId)

          const productIds = [...multiVariantIds, ...singleVariantIds, ...multiProductIds, ...neckwareIds]
            .filter(Boolean)
            .map(Number)

          const response = await supercycleFetch<AvailabilityResponse>('POST', '/product_availability_checks', {
            body: JSON.stringify({
              product_ids: productIds,
              rental_start: startDate,
              rental_end: endDate,
            })
          })

          // Store the checked dates
          this.lastCheckedDates = { startDate, endDate }

          // Update each variant step with its available variants
          response.products.forEach(product => {
            console.log(`Processing product ${product.shopifyId} with variants:`, product.availableVariants)
            const variantStep = this.querySelector(`multi-variant-step[data-product-id="${product.shopifyId}"]`) as MultiVariantStep | null
            const productInMultiStep = this.querySelector(`multi-product-step .product-configurator__step-product[data-product-id="${product.shopifyId}"]`)
            const productInNeckwareStep = this.querySelector(`neckware-step .product-configurator__step-product[data-product-id="${product.shopifyId}"]`)

            if (variantStep) {
              const availableVariantIds = product.availableVariants.map(v => v.shopifyId)
              variantStep.updateAvailableVariants(availableVariantIds)
            }

            if (productInMultiStep) {
              const multiStep = productInMultiStep.closest('multi-product-step') as MultiProductStep
              const availableVariantIds = product.availableVariants.map(v => v.shopifyId)
              multiStep.updateAvailableVariants(product.shopifyId, availableVariantIds)
            }

            if (productInNeckwareStep) {
              const neckwareStep = productInNeckwareStep.closest('neckware-step') as NeckwareStep
              const availableVariantIds = product.availableVariants.map(v => v.shopifyId)
              neckwareStep.updateAvailableVariants(String(product.shopifyId), availableVariantIds)
            }
          })
        } catch (error) {
          console.error('Failed to check product availability:', error)
          return
        } finally {
          this.setButtonLoading(button, false)
        }
      }
    }

    // Handle single variant/product steps
    if (this.activeStep instanceof MultiVariantStep) {
      if (!this.activeStep.isAvailable()) {
        // Skip rental intent creation for unavailable products
        this.activeStep.deactivate()
        this.activeStep.complete()
        const nextStepIndex = this.activeStep.getIndex() + 1
        const nextStep = this.steps.find(step => step.getIndex() === nextStepIndex)
        if (nextStep) {
          nextStep.activate()
          this.activeStep = nextStep
        }
        return
      }
    }

    if (this.activeStep instanceof MultiVariantStep ||
        this.activeStep instanceof NeckwareStep ||
        (this.activeStep instanceof MultiProductStep && !this.activeStep.dataset.allowMultiple)) {
      try {
        this.setButtonLoading(button, true)

        const variantId = this.activeStep.getCurrentVariantId()
        console.log('Creating rental intent for variant:', variantId)

        // Get rental dates from rental step
        const rentalStep = this.steps[0] as RentalStep
        const { startDate } = rentalStep.getDates()
        const duration = rentalStep.getSelectedDuration()
        console.log('Rental details:', { startDate, duration })

        // Create rental intent if not created yet or if dates changed
        const datesChanged = !this.lastCheckedDates ||
          this.lastCheckedDates.startDate !== startDate

        if (!this.activeStep.hasRentalIntent() || datesChanged) {
          const rentalPeriod = this.activeStep.getRentalPeriodByDuration(duration)
          console.log('Selected rental period:', rentalPeriod)

          if (!rentalPeriod?.global_id) {
            const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)
            bodyElement.showNotification('Unable to proceed: No rental period available for selected duration', 'warning')
            this.setButtonLoading(button, false)
            return
          }

          const response = await supercycleFetch<RentalIntentResponse>('POST', '/rental_intents', {
            body: JSON.stringify({
              variant_id: variantId,
              option: {
                globalId: rentalPeriod.global_id,
                params: {
                  rental_start: startDate,
                }
              }
            })
          })

          console.log('Rental intent response:', response)
          const productId = this.activeStep.dataset.productId || ''
          this.activeStep.setRentalIntentToken(response, productId)
          console.log('Set rental intent token for product:', productId, 'Token:', this.activeStep.getRentalIntentToken(productId))
        } else {
          console.log('Skipping rental intent creation - dates unchanged')
        }

      } catch (error) {
        console.error('Failed to create rental intent:', error)
        return
      } finally {
        this.setButtonLoading(button, false)
      }
    }

    // Handle multiple product selection steps
    if (this.activeStep instanceof MultiProductStep && this.activeStep.dataset.allowMultiple) {
      try {
        this.setButtonLoading(button, true)
        const selectedProducts = this.activeStep.getCurrentVariantIds()

        // Get rental dates from rental step
        const rentalStep = this.steps[0] as RentalStep
        const { startDate } = rentalStep.getDates()
        const duration = rentalStep.getSelectedDuration()

        // Create rental intent for each selected product
        for (const [productId, variantId] of selectedProducts) {
          if (!this.activeStep.hasRentalIntent(productId)) {
            const rentalPeriod = this.activeStep.getRentalPeriodByDuration(duration, productId)
            if (!rentalPeriod) throw new Error('No matching rental period found')

            const response = await supercycleFetch<RentalIntentResponse>('POST', '/rental_intents', {
              body: JSON.stringify({
                variant_id: variantId,
                option: {
                  globalId: rentalPeriod.global_id,
                  params: {
                    rental_start: startDate,
                  }
                }
              })
            })

            this.activeStep.setRentalIntentToken(response, productId)
          }
        }
      } catch (error) {
        console.error('Failed to create rental intents:', error)
        return
      } finally {
        this.setButtonLoading(button, false)
      }
    }

    // Store selected variant if it's a multi-variant step
    if (this.activeStep instanceof MultiVariantStep) {
      const variantId = this.activeStep.getCurrentVariantId()
      if (variantId && this.activeStep.dataset.productId) {
        this.selectedVariants[this.activeStep.dataset.productId] = variantId
      }
    } else if (this.activeStep instanceof MultiProductStep) {
      const selectedProducts = this.activeStep.getCurrentVariantIds()
      selectedProducts.forEach((variantId, productId) => {
        this.selectedVariants[productId] = variantId
      })
    } else if (this.activeStep instanceof NeckwareStep) {
      try {
        this.setButtonLoading(button, true);

        const variantId = this.activeStep.getCurrentVariantId();
        console.log('Creating rental intent for neckware variant:', variantId);

        // Get rental dates from rental step
        const rentalStep = this.steps[0] as RentalStep;
        const { startDate } = rentalStep.getDates();
        const duration = rentalStep.getSelectedDuration();
        console.log('Neckware rental details:', { startDate, duration });

        // Get rental period
        const rentalPeriod = this.activeStep.getRentalPeriodByDuration(duration);
        console.log('Neckware rental period:', rentalPeriod);

        if (!rentalPeriod?.global_id) {
          console.error('No rental period found for neckware');
          return;
        }

        const response = await supercycleFetch<RentalIntentResponse>('POST', '/rental_intents', {
          body: JSON.stringify({
            variant_id: variantId,
            option: {
              globalId: rentalPeriod.global_id,
              params: {
                rental_start: startDate,
              }
            }
          })
        });

        console.log('Neckware rental intent response:', response);
        const productId = this.activeStep.getSelectedProductId();
        console.log('Setting rental intent for neckware product:', productId);
        if (productId) {
          this.activeStep.setRentalIntentToken(response, productId);
          this.activeStep.setHasCreatedRentalIntent(true);
          this.selectedVariants[productId] = variantId!;
        }
      } catch (error) {
        console.error('Failed to create rental intent for neckware:', error);
        return;
      } finally {
        this.setButtonLoading(button, false);
      }
    }

    // Before proceeding with step navigation
    if (this.activeStep instanceof MultiVariantStep) {
      const details = this.activeStep.getProductDetails()
      this.selectedProductDetails.set(details.productId, {
        title: details.title,
        options: details.options
      })
    } else if (this.activeStep instanceof MultiProductStep) {
      const details = this.activeStep.getProductDetails()
      details.forEach(({ productId, title, options }) => {
        this.selectedProductDetails.set(productId, { title, options })
      })
    } else if (this.activeStep instanceof NeckwareStep) {
      const details = this.activeStep.getProductDetails()
      details.forEach(({ productId, title, options }) => {
        this.selectedProductDetails.set(productId, { title, options })
      })
    }

    // Proceed with step navigation
    console.log('Proceeding with navigation')
    this.activeStep.deactivate()
    this.activeStep.complete()

    const nextStepIndex = this.activeStep.getIndex() + 1
    console.log('Next step index:', nextStepIndex)
    const nextStep = this.steps.find(step => step.getIndex() === nextStepIndex)
    console.log('Found next step:', nextStep)

    if (nextStep) {
      nextStep.activate()
      this.activeStep = nextStep
    } else {
      // This was the last step, update summary
      this.updateSummary()
    }

    // Update add to cart button state
    this.updateAddToCartButton()
  }

  /**
   * Handles clicks on completed steps
   * Allows navigation back to previous completed steps
   * @param event Mouse event from step click
   */
  private handleCompletedStepClick(event: MouseEvent) {
    const clickedStep = event.currentTarget as ConfiguratorStep

    // Only handle clicks on the step header, not the entire step
    const isHeaderClick = (event.target as HTMLElement).closest('.product-configurator__step-header')
    if (!isHeaderClick) return

    if (clickedStep.classList.contains('product-configurator__step--completed')) {
        // Clear the selected variants and details for this step and all subsequent steps
        const clickedIndex = clickedStep.getIndex()
        this.steps.forEach(step => {
            if (step.getIndex() >= clickedIndex) {
                // Clear data for this step
                if (step instanceof MultiVariantStep) {
                    const productId = step.dataset.productId
                    if (productId) {
                        delete this.selectedVariants[productId]
                        this.selectedProductDetails.delete(productId)
                    }
                } else if (step instanceof MultiProductStep) {
                    const products = step.getCurrentVariantIds()
                    products.forEach((_, productId) => {
                        delete this.selectedVariants[productId]
                        this.selectedProductDetails.delete(productId)
                    })
                    // Reset the rental intent tokens for this step
                    step.clearRentalIntentTokens()
                } else if (step instanceof NeckwareStep) {
                    const productId = step.getSelectedProductId()
                    if (productId) {
                        delete this.selectedVariants[productId]
                        this.selectedProductDetails.delete(productId)
                    }
                }
                
                // Remove completed status from subsequent steps
                if (step.getIndex() > clickedIndex) {
                    step.classList.remove('product-configurator__step--completed')
                }
            }
        })

        this.activeStep?.deactivate()
        clickedStep.activate()
        this.activeStep = clickedStep
        
        // Update the summary with the remaining selections
        this.updateSummary()
        this.updateAddToCartButton()
    }
  }

  private async handleAddToCart() {
    console.log('handleAddToCart called')
    if (!this.addToCartButton) {
      console.log('No add to cart button found')
      return
    }

    // Log all selected products
    console.log('Selected product details:', this.selectedProductDetails)
    console.log('Selected variants:', this.selectedVariants)

    this.addToCartButton.classList.add('product-configurator__add-to-cart-button--loading')
    this.addToCartButton.disabled = true

    try {
      // Get base items from selected variants
      const items: Array<{
        id: number;
        quantity: number;
        properties?: { _rental_intent_token: string };
        selling_plan?: string;
      }> = Object.entries(this.selectedVariants).map(([productId, variantId]) => {
        console.log(`Processing product: ${productId} with variant: ${variantId}`)
        
        const step = this.steps.find(s => 
          (s instanceof MultiVariantStep && s.dataset.productId === productId) ||
          (s instanceof MultiProductStep && s.querySelector(`[data-product-id="${productId}"]`)) ||
          (s instanceof NeckwareStep && s.querySelector(`[data-product-id="${productId}"]`))
        )
        
        console.log(`Found step for product ${productId}:`, step)

        let rentalIntent = ''
        if (step instanceof MultiProductStep && step.dataset.allowMultiple === 'true') {
          const allTokens = step.getAllRentalIntentTokens()
          rentalIntent = allTokens.get(productId) || ''
        } else {
          rentalIntent = step?.getRentalIntentToken(productId) || ''
        }

        console.log(`Rental intent for product: ${productId} is ${rentalIntent}`)

        // Extract selling_plan_id from rentalIntent
        let sellingPlanId = ''
        if (rentalIntent) {
          try {
            const rentalIntentData = JSON.parse(rentalIntent)
            sellingPlanId = rentalIntentData.selling_plan_id || ''
          } catch (error) {
            console.error('Failed to parse rental intent:', error)
          }
        }

        // Only return item if sellingPlanId is available
        if (sellingPlanId) {
          return {
            id: variantId,
            quantity: 1,
            properties: {
              '_rental_intent_token': rentalIntent
            },
            selling_plan: sellingPlanId
          }
        }

        return null
      }).filter(item => item !== null) // Filter out null items

      // Check if insurance is selected and add it to items
      const insuranceCheckbox = this.querySelector('#product-configurator__insurance-input') as HTMLInputElement
      const insuranceProductId = (this.querySelector('.product-configurator__insurance') as HTMLElement)?.dataset.insuranceProductId

      if (insuranceCheckbox?.checked && insuranceProductId) {
        items.push({
          id: Number(insuranceProductId),
          quantity: 1
        })
      }

      const formData = {
        items,
        sections: this.cartId,
        sections_url: window.location.pathname
      };

      console.log('Cart add payload details:', {
        items: items.map(item => ({
          id: item.id,
          hasRentalIntent: !!item.properties?._rental_intent_token,
          sellingPlan: item.selling_plan
        })),
        selectedVariants: this.selectedVariants,
        rentalIntents: Array.from(this.steps)
          .filter(step => step instanceof NeckwareStep)
          .map(step => (step as NeckwareStep).getAllRentalIntentTokens())
      });

      if (formData.items.length === 0) {
        console.log('No items to add to cart')
        return
      }

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(formData)
      })

      const parsedState = await response.json()
      console.log('Cart add response:', parsedState)

      if (parsedState.description) {
        console.log('Error from cart add:', parsedState.description)
        this.showError(parsedState)
        return
      }

      this.lastParsedState = parsedState
      this.updateCartItems(parseHTML(parsedState.sections[this.cartId]))
      this.resetConfigurator()

    } catch (error) {
      console.error('Error adding items to cart:', error)
    } finally {
      console.log('Add to cart process completed')
      this.addToCartButton.classList.remove('product-configurator__add-to-cart-button--loading')
      this.addToCartButton.disabled = false
      this.updateAddToCartButton()
    }
  }

  private resetConfigurator() {
    // Reset all steps
    this.steps.forEach(step => {
      step.deactivate()
      step.classList.remove('product-configurator__step--completed')
      // Call reset on each step to handle step-specific cleanup
      step.reset()
    })

    // Reset to first step
    this.activeStep = this.steps[0]
    this.activeStep.activate()

    // Reset selected variants
    this.selectedVariants = {}

    // Reset add to cart button
    if (this.addToCartButton) {
      const buttonText = this.addToCartButton.querySelector('.product-configurator__add-to-cart-button-text')
      if (buttonText) {
        buttonText.textContent = 'Select Product(s)'
      }
      this.addToCartButton.disabled = true
    }

    // Reset last checked dates
    this.lastCheckedDates = null

    // Clear and hide summary
    if (this.summaryList) {
      this.summaryList.innerHTML = ''
      const summary = this.summaryList.closest('.product-configurator__summary')
      summary?.classList.remove('product-configurator__summary--active')
    }

    // Clear selected product details
    this.selectedProductDetails.clear()
  }

  private getQueryConfig(items: { id: number; quantity: number }[]) {
    const config: any = fetchConfig('javascript')
    const formData = new FormData()

    formData.append('items', JSON.stringify(items))
    formData.append('sections', this.cartId)
    formData.append('sections_url', window.location.pathname)

    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    return config
  }

  private updateCartItems(html) {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)
    const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)

    if (cart) {
      this.updateCart(html)
    } else if (cartDrawer) {
      this.updateCartDrawer(html)
    } else if (popup) {
      this.updatePopup(html)
    } else {
      this.updateCartCount(html)
    }
  }

  private updateCart(html): void {
    const cart: CartPage = $el(CART_PAGE_SELECTOR)
    cart?.updateEmptyStatus(false)
    cart?.purchaseHandler(html, this.lastParsedState)
  }

  private updateCartDrawer(html): void {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    cartDrawer?.updateAsideProductsByParsedState(this.lastParsedState)
    cartDrawer?.updateEmptyStatus()
    cartDrawer?.purchaseHandler(html, this.lastParsedState)
  }

  private updatePopup(html): void {
    const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)
    popup?.purchaseHandler(html, this.lastParsedState)
  }

  private updateCartCount(html): void {
    const newCount = $el('#NewCartCount', html)
    const currentCounts = $list('#CartCount')

    currentCounts.forEach(currentCount => {
      replaceNodeChildren(currentCount, newCount)
    })
  }

  private showError(parsedState) {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    const error =
      typeof parsedState.description === 'object'
        ? this.getErrorFromObject(parsedState.description)
        : parsedState.description

    bodyElement.showNotification(error, 'warning')
  }

  private getErrorFromObject(errorObject: object) {
    return Object.keys(errorObject)
      .map(key => {
        return `${key}: ${errorObject[key]}`
      })
      .join(' , ')
  }

  get cartId(): string {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)

    if (cartDrawer) {
      return cartDrawer.dataset.sectionId
    } else if (cart) {
      return cart.dataset.sectionId
    } else {
      return 'cart-notification-popup-content'
    }
  }

  private updateAddToCartButton() {
    if (!this.addToCartButton) return

    const buttonText = this.addToCartButton.querySelector('.product-configurator__add-to-cart-button-text')
    if (!buttonText) return

    const allStepsComplete = this.steps.every(step =>
      step.classList.contains('product-configurator__step--completed')
    )
    this.addToCartButton.disabled = !allStepsComplete

    if (allStepsComplete) {
      buttonText.textContent = 'Add to cart'
    } else {
      buttonText.textContent = 'Select Product(s)'
    }
  }

  private updateSummary() {
    if (!this.summaryList) return

    // Clear existing items
    this.summaryList.innerHTML = ''

    // Add rental details first
    const rentalStep = this.steps[0] as RentalStep
    if (rentalStep) {
        const { startDate, endDate } = rentalStep.getDates()
        const duration = rentalStep.getSelectedDuration()
        
        const rentalLi = document.createElement('li')
        rentalLi.className = 'product-configurator__summary-item product-configurator__summary-item--rental'
        
        // Format dates for display without adding extra days
        const formatDate = (dateStr: string) => {
            const [year, month, day] = dateStr.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return date.toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
            })
        }
        
        const formattedStartDate = formatDate(startDate)
        const formattedEndDate = formatDate(endDate)
        
        rentalLi.textContent = `${duration} Day Rental: ${formattedStartDate} - ${formattedEndDate}`
        this.summaryList.appendChild(rentalLi)
    }

    // Add items for each selected product
    this.selectedProductDetails.forEach(({ title, options }, productId) => {
        const li = document.createElement('li')
        li.className = 'product-configurator__summary-item'

        const optionsText = Object.values(options).join(' / ')
        li.textContent = optionsText ? `${title} - ${optionsText}` : title
        this.summaryList.appendChild(li)
    })

    // Show summary section
    const summary = this.summaryList.closest('.product-configurator__summary')
    summary?.classList.add('product-configurator__summary--active')
  }
}